import {useEffect} from 'react';
import {i18n} from 'next-i18next';

import {
	ColorModeScript,
	extendTheme,
	TempoProvider,
	type ColorMode,
} from '@tempo/core';
import {useRouter} from 'next/router';

const getDirection = (locale: string): 'rtl' | 'ltr' => {
	return i18n?.dir(locale) || 'ltr';
};

const forcedDarkColorModeManager = {
	type: 'localStorage' as const,
	ssr: false,
	get: (): ColorMode => 'dark',
	set: (): void => {
		/* no-op: dark mode is forced */
	},
};

export const ThemeProvider = ({children}: {children?: React.ReactNode}) => {
	const {locale} = useRouter();

	const direction = getDirection(locale || 'en');

	const theme = extendTheme({
		config: {
			direction,
			initialColorMode: 'dark',
			useSystemColorMode: false,
		},
		fonts: {
			heading: `'Deezer Narrow', 'Inter', Arial, sans-serif`,
			body: `'Inter', 'Deezer Product', 'Open Sans', Arial, sans-serif`,
		},
	});

	useEffect(() => {
		document.querySelector('body')?.setAttribute('dir', direction);
		try {
			window.localStorage.setItem('chakra-ui-color-mode', 'dark');
		} catch {
			/* localStorage unavailable */
		}
	}, [direction]);

	return (
		<>
			<ColorModeScript initialColorMode={theme.config.initialColorMode} />
			<TempoProvider
				theme={theme}
				colorModeManager={forcedDarkColorModeManager}
			>
				{children}
			</TempoProvider>
		</>
	);
};
